import * as React from 'react';
import { observer } from 'mobx-react';
import { Sku } from '../../domain/entities/Sku';
import { config, translate, ITranslationsCollection } from 'elc-service';
import { Desktop, Mobile } from 'elc-layouts';
import { Breakpoints, ContentSpacing, breakpoint } from 'elc-base-theme';
import ProductSizePicker, {
    ISizePicker,
    ISizePickerConfigs
} from '../product-size-picker/ProductSizePicker';
import ProductCTA, {
    IProductFullAddToBagButtonConfig
} from '../../../exported/product-cta/ProductCTA';
import styled from 'styled-components';
import { StickyAddToBagContainerTheme } from '../../../theme/default-theme';
import { Heading5, Subtitle1 } from 'elc-typography';
import { ProductShadesSidebarButton } from '../product-shades-sidebar/ProductShadesSidebarButton';
import { ProductImages } from 'elc-images';
import { Column, Container, Row } from 'elc-grid';
import { ProductShadePickerComponent } from '../common/ProductShadePickerComponent';
import { classes } from '../../utils/Classes';
import {
    STICKY_PRODUCT_SIZE,
    STICKY_PRODUCT_NAME,
    STICKY_PRODUCT_PRICE
} from '../../constants/DataTestIdAttributes';
import { IProductShadesSidebarProps } from '../product-shades-sidebar/ProductShadesSidebar';
import { ProductPriceFormatted } from '../common/ProductPriceFormatted';
import { ComponentTypes } from '../../constants/ComponentTypes';
import { diContainer, serviceNames } from '../../../service-setup/diContainer';
import { Debounce } from '../../utils/Debounce';
import { PreorderMessage } from '../common/PreorderMessage';
import { NotifyMeCTA, IProductNotifyMeCTA } from '../product-notify-me/NotifyMeCTA';
import { LINK } from '../../constants/CTATypes';
import { IProductQuickViewContentConfig } from '../product-quickview/ProductQuickViewContent';

export interface IProductStickyAddToBagConfig extends IProductFullAddToBagButtonConfig {
    enableShadesSidebar?: boolean;
    enableShadesDropdown?: boolean;
    hideStickyProductSummaryInfo?: boolean;
    showPriceOnStickyAddToBag?: boolean;
    sizePicker?: ISizePickerConfigs;
    handleShadeSelect(name: string, productId: string): void;
}

export interface IProductStickyAddToBag {
    classNames?: string;
    selectedSku: Sku;
    quantity: number;
    productSizePickerProps: ISizePicker;
    productShadesSidebar: IProductShadesSidebarProps;
    stickyAddToBagVisibleClassName: string;
    showStickyAddToBag: boolean;
    setShowStickyAddToBag(value: boolean): void;
    config: IProductStickyAddToBagConfig;
    autoReplenishOptions: number[];
    selectedAutoReplenishOption: number;
    translations?: ITranslationsCollection;
}

const StyledStickyAddToBag = styled.div`
    padding: 0;
    width: inherit;
    transform: translateY(0);
    ${StickyAddToBagContainerTheme}

    &.sticky-visible {
        transform: translateY(-100%);
        transition: transform 0.4s;
    }
`;

const StyledStickyContainer = styled(Container)`
    height: 100%;
    margin: 0;
`;

const StyledImageColumn = styled(Column)`
    height: 100%;
`;

const StyledRow = styled(Row)`
    padding: ${ContentSpacing.space10} 0;
    height: 100%;
    align-items: flex-start;
`;

const StyledNamePriceColumn = styled(Column)`
    text-align: left;

    [dir='rtl'] & {
        text-align: right;
    }
`;

const DropdownWrapper = styled.div`
    ${(props: { hasMultiple: boolean }) => !props.hasMultiple && 'pointer-events: none'};
    margin-right: ${ContentSpacing.space32};
    & .elc-dropdown {
        margin: 0;
    }
    & .elc-grid-column {
        padding: 0;
    }
    & .elc-dropdown-options {
        text-align: left;
        [dir='rtl'] & {
            text-align: right;
            margin-right: 0;
            margin-left: ${ContentSpacing.space32};
        }
    }
`;

const ProductShadesSidebarWrapper = styled.div`
    margin-left: 0;
    margin-right: 0;
    ${breakpoint('desktop')`
        margin-right: ${ContentSpacing.space32};
        [dir='rtl'] & {
            margin-right: 0;
            margin-left: ${ContentSpacing.space32};
        }
    `};
`;

const StyledPriceAndSizeWrapper = styled.div`
    display: flex;
`;

const ProductImageWrapper = styled.div`
    height: 100%;
    ${breakpoint('desktop')`
        width: 50%;
        margin: 0 auto;
    `};
`;

const StyledProductFullAddToBagButton = styled.div`
    .elc-product-full-sticky-product-add-to-bag-wrapper {
        display: flex;
        flex-direction: column-reverse;
    }
    .elc-add-to-bag-sku-inventory-status-message {
        margin: ${ContentSpacing.space10} 0 0;
    }
`;

@config(['sizePicker'])
@translate(['notifyWhenAvailable'])
@observer
export class ProductStickyAddToBag extends React.Component<IProductStickyAddToBag> {
    private ctaRef: Element | null;
    private debounceClass: Debounce;

    constructor(props: IProductStickyAddToBag) {
        super(props);
        this.debounceClass = new Debounce();
    }

    private handleScroll = () => {
        const { showStickyAddToBag, setShowStickyAddToBag } = this.props;
        const ctaPosition = this.ctaRef && this.ctaRef.getBoundingClientRect();
        const isCtaInView = ctaPosition && ctaPosition.bottom >= 0;

        if (isCtaInView && showStickyAddToBag) {
            setShowStickyAddToBag(false);
        } else if (!isCtaInView && !showStickyAddToBag) {
            setShowStickyAddToBag(true);
        }
    };

    private debounceScroll = () => {
        this.debounceClass.debounce(this.handleScroll, 150);
    };

    private setActiveStickyContainerClassName() {
        document.documentElement.classList.add('elc-sticky-container--active');
    }

    public async componentDidMount() {
        this.ctaRef = document.querySelector('.elc-product-full-cta-wrapper');
        this.setActiveStickyContainerClassName();
        window.addEventListener('scroll', this.debounceScroll, {
            capture: true,
            passive: true
        });
        this.handleScroll();
    }

    public async componentWillUnmount() {
        window.removeEventListener('scroll', this.debounceScroll);
    }

    private getProductNamePriceInfo() {
        const {
            selectedSku,
            config: { hideStickyProductSummaryInfo }
        } = this.props;
        const { parentProduct, selectedSize } = selectedSku;
        const { displayName } = parentProduct;
        const productNamePriceClass = classes('sticky-menu-product-name-price');

        const { formattedPrice, formattedOriginalPrice } = selectedSku;

        const productNamePriceInfo = () => (
            <>
                <Heading5
                    data-test-id={STICKY_PRODUCT_NAME}
                    dangerouslySetInnerHTML={{ __html: displayName }}
                />
                <StyledPriceAndSizeWrapper data-test-id={STICKY_PRODUCT_PRICE}>
                    <ProductPriceFormatted
                        formattedPrice={formattedPrice}
                        formattedOriginalPrice={formattedOriginalPrice}
                    />
                    <Subtitle1 data-test-id={STICKY_PRODUCT_SIZE}>{selectedSize.value}</Subtitle1>
                </StyledPriceAndSizeWrapper>
            </>
        );

        return (
            <StyledNamePriceColumn xs={12} sm={4} lg={4} className={productNamePriceClass}>
                <Desktop minWidth={Breakpoints.desktop}>{productNamePriceInfo}</Desktop>
                <Mobile maxWidth={Breakpoints.desktop - 1}>
                    {() => !hideStickyProductSummaryInfo && productNamePriceInfo()}
                </Mobile>
            </StyledNamePriceColumn>
        );
    }

    private getProductImage() {
        const { selectedSku } = this.props;

        return (
            <Desktop minWidth={Breakpoints.desktop}>
                {() => (
                    <StyledImageColumn xs={1} sm={1} lg={1}>
                        <ProductImageWrapper>
                            <ProductImages
                                imgPath={selectedSku.defaultSmallImage.src}
                                altText={selectedSku.defaultSmallImage.alt}
                            />
                        </ProductImageWrapper>
                    </StyledImageColumn>
                )}
            </Desktop>
        );
    }

    private getNotifyMeLink() {
        const { selectedSku, config: notifyMeConfig, translations } = this.props;
        const { enableNotifyMe } = notifyMeConfig as IProductQuickViewContentConfig;
        const { notifyWhenAvailable } = translations as ITranslationsCollection;
        const ctaType = LINK;

        const notifyMeCTAProps: IProductNotifyMeCTA = {
            selectedSku: selectedSku,
            config: {
                enableNotifyMe,
                customNotifyMeLabel: notifyWhenAvailable,
                ctaType
            }
        };

        return <NotifyMeCTA {...notifyMeCTAProps} />;
    }

    private getProductCTA() {
        const {
            selectedSku,
            productSizePickerProps,
            productShadesSidebar,
            config: { sizePicker, enableShadesSidebar, enableShadesDropdown },
            quantity,
            selectedAutoReplenishOption
        } = this.props;
        const options = sizePicker ? sizePicker.product_sticky_add_to_bag : {};
        const stickySizePickerProps = { ...productSizePickerProps, options };

        const {
            parentProduct,
            shades,
            sizes,
            selectedShade,
            isPreorderEnabled,
            isPreorderable,
            preorderDate
        } = selectedSku;
        const { hasMultipleSizes, isShaded } = parentProduct;
        const isSized = sizes.length > 0;
        const hasMultipleShades = shades.length > 1;
        const showLeftCTA = enableShadesSidebar && isShaded;
        const shadesSidebarWrapperClassName = classes('product-shades-sidebar-wrapper');
        const shadesDropdownWrapperClassName = classes('product-shades-dropdown-wrapper');

        const leftColumnCTA = () => (
            <Column xs={6} sm={3} md={3} lg={3}>
                {!isShaded && isSized && (
                    <Desktop minWidth={Breakpoints.desktop}>
                        {() => (
                            <DropdownWrapper
                                className={shadesDropdownWrapperClassName}
                                hasMultiple={hasMultipleSizes}
                            >
                                <ProductSizePicker {...stickySizePickerProps} />
                            </DropdownWrapper>
                        )}
                    </Desktop>
                )}
                {isShaded && productShadesSidebar.config.isButtonModal && (
                    <ProductShadesSidebarWrapper className={shadesSidebarWrapperClassName}>
                        <ProductShadesSidebarButton selectedShade={selectedShade} />
                    </ProductShadesSidebarWrapper>
                )}
                {enableShadesDropdown && (
                    <DropdownWrapper hasMultiple={hasMultipleShades}>
                        {this.getProductShadePickerComponent()}
                    </DropdownWrapper>
                )}
            </Column>
        );

        const ctaConfig = {
            autoReplenishOptions: [],
            quantity,
            skuId: selectedSku.skuId,
            isDisplayedOnSticky: true,
            replenishment: selectedAutoReplenishOption,
            ...this.props.config
        };

        const displayNotifyMeLink = selectedSku.isPreorderEnabled && !selectedSku.isShoppable;

        return (
            <>
                <Desktop minWidth={Breakpoints.desktop}>{leftColumnCTA}</Desktop>
                {showLeftCTA && <Mobile maxWidth={Breakpoints.desktop - 1}>{leftColumnCTA}</Mobile>}
                <Column xs={showLeftCTA ? 6 : 12} sm={3} md={3} lg={3}>
                    <StyledProductFullAddToBagButton>
                        <ProductCTA
                            config={ctaConfig}
                            viewController={diContainer.get(serviceNames.productCTAViewController)}
                        />
                    </StyledProductFullAddToBagButton>
                    {isPreorderEnabled && (
                        <PreorderMessage
                            preorderDate={preorderDate}
                            isPreorderActive={isPreorderable}
                        />
                    )}
                    {displayNotifyMeLink && this.getNotifyMeLink()}
                </Column>
            </>
        );
    }

    private getProductShadePickerComponent() {
        const { handleShadeSelect } = this.props.config;
        const { selectedSku } = this.props;

        const {
            parentProduct: { isShaded, shades, productId },
            selectedShade
        } = selectedSku;

        const dropdownComponentConfig = {
            componentType: ComponentTypes.PRODUCT_QUICKSHOP,
            shadeDropdownProps: {
                selectedShade,
                selectedHexValue: selectedShade.hexVal,
                shades,
                onShadeSelect: handleShadeSelect,
                productId,
                useDropup: true
            },
            viewController: diContainer.get(serviceNames.productShadePickerComponentViewController)
        };

        return isShaded && <ProductShadePickerComponent {...dropdownComponentConfig} />;
    }

    public render() {
        const { stickyAddToBagVisibleClassName: stickyAddToBagClassName, selectedSku } = this.props;
        const {
            parentProduct: { isShaded }
        } = selectedSku;
        const stickyMenuShadedProductClassName = isShaded
            ? classes('product-full-sticky-menu-shaded')
            : '';
        const stickyMenuClassName = classes(
            'product-full-sticky-menu',
            stickyAddToBagClassName,
            stickyMenuShadedProductClassName
        );

        return (
            <StyledStickyAddToBag id="sticky-add-to-bag-component" className={stickyMenuClassName}>
                <StyledStickyContainer>
                    <StyledRow>
                        {this.getProductImage()}
                        {this.getProductNamePriceInfo()}
                        <Desktop minWidth={Breakpoints.desktop}>
                            {() => <Column xs={3} sm={1} md={1} lg={1} />}
                        </Desktop>
                        {this.getProductCTA()}
                    </StyledRow>
                </StyledStickyContainer>
            </StyledStickyAddToBag>
        );
    }
}
